import type { S3Storage } from '@tunasong/schemas'

/** The existing shape of an entity's `storage` field (spec §7) — not a new type. */
export type StorageRef = S3Storage

/**
 * The document-blob seam. Y.js documents are the one blob class the kernel cannot
 * do without: the merge job reads the stored doc, applies incremental updates, and
 * writes it back. S3 in the cloud, the local filesystem on a local host.
 */
export interface DocStore {
  storeDoc(props: { entityId: string; userId: string; docType: string; yDocAsUpdates: Uint8Array }): Promise<StorageRef>
  getDoc(props: { storage: StorageRef; fallbackToLatest?: boolean }): Promise<Uint8Array | null>
  /**
   * Type predicate, matching S3Doc.isDocPath: a true result narrows `storage` to
   * present, which callers rely on to pass it to getDoc without a non-null assertion.
   */
  isDocPath(props: { entityId?: string; userId: string; storage?: StorageRef }): props is {
    entityId: string
    userId: string
    storage: StorageRef
  }
}

export type DocStoreKind = 's3' | 'fs'
export type DocStoreFactory = () => DocStore

const registry = new Map<DocStoreKind, DocStoreFactory>()

export const registerDocStore = (kind: DocStoreKind, factory: DocStoreFactory) => {
  registry.set(kind, factory)
}

/** Test-only helper */
export const resetDocStoreRegistry = () => registry.clear()

const resolve = (): DocStore => {
  const kind = (process.env.TUNA_DOC_STORE ?? 's3') as DocStoreKind
  const factory = registry.get(kind)
  if (!factory) {
    throw new Error(`No doc store registered for TUNA_DOC_STORE=${kind}. Import the implementation's register module.`)
  }
  return factory()
}

/**
 * Lazily-resolved handle — same pattern as createEntityStore, so module-scope
 * construction in handlers works before any registration has run.
 */
export const createDocStore = (): DocStore => {
  let instance: DocStore | null = null
  const get = () => (instance ??= resolve())
  return new Proxy({} as DocStore, {
    get: (_target, prop) => {
      const store = get()
      const value = store[prop as keyof DocStore]
      return typeof value === 'function' ? value.bind(store) : value
    },
  })
}
